/**
 * Platform allowlists aligned with Upload-Post REST API (`POST /api/upload`, etc.).
 */
export type ManualPostType = "video" | "photo" | "text" | "document";

/** `platform[]` values for `POST /api/upload` (video). */
export type VideoPlatform =
	| "tiktok"
	| "instagram"
	| "youtube"
	| "facebook"
	| "linkedin"
	| "x";

/** `platform[]` values for `POST /api/upload_photos`. */
export type PhotoPlatform =
	| "tiktok"
	| "instagram"
	| "facebook"
	| "linkedin"
	| "x";

/** `platform[]` values for `POST /api/upload_text`. */
export type TextPlatform = "x" | "linkedin" | "facebook";

/** `GET /api/uploadposts/users` (shape varies; kept loose for forward compatibility). */
export interface UsersResponse {
	success?: boolean;
	profiles?: Array<{
		username?: string;
		social_accounts?: Record<string, unknown>;
		created_at?: string;
		[key: string]: unknown;
	}>;
	[key: string]: unknown;
}

const MANUAL_VIDEO = new Set<string>([
	"tiktok",
	"instagram",
	"youtube",
	"facebook",
	"linkedin",
	"x",
]);

const MANUAL_PHOTO = new Set<string>([
	"tiktok",
	"instagram",
	"facebook",
	"linkedin",
	"x",
]);

const MANUAL_TEXT = new Set<string>(["x", "linkedin", "facebook"]);

const MANUAL_DOCUMENT = new Set<string>(["linkedin"]);

export function getManualPostTypePlatformSet(
	type: ManualPostType,
): Set<string> {
	if (type === "video") return MANUAL_VIDEO;
	if (type === "photo") return MANUAL_PHOTO;
	if (type === "text") return MANUAL_TEXT;
	return MANUAL_DOCUMENT;
}

/** Normalize like the web `normalizePlatformKey` (lowercase, underscores). */
export function normalizeUploadPlatformKey(platform: string): string {
	return platform.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/**
 * Keep only platform keys that are valid for the given post type and were requested.
 */
export function filterPlatformsForManualPostType(
	normalizedKeys: string[],
	type: ManualPostType,
): string[] {
	const allowed = getManualPostTypePlatformSet(type);
	return normalizedKeys
		.map((k) => normalizeUploadPlatformKey(k))
		.filter((k, i, a) => a.indexOf(k) === i && allowed.has(k));
}
